import {
    FilterAccordionButton,
    FilterAccordionItem,
    FilterCheckbox,
} from '../FilterAccordion';
import {
    clusterFilterOptions,
    healthStatFilterOptions,
    instagramClusterFilterOptions,
    tikTokClusterFilterOptions,
} from 'utils/filters';
import { forwardRef, useMemo, useState } from 'react';
import styled, { css } from 'styled-components';
import {
    useFetchFlavors,
    useFetchInstagramRelatedAffinityRoots,
    useFetchPopIndexAffinityRoots,
    useFetchTikTokAffinityRoots,
    useRelatedSearchGroupsWithData,
} from 'utils/hooks/api';
import { FiChevronDown } from 'react-icons/fi';
import { MdSort } from 'react-icons/md';
import { Popover } from '@headlessui/react';

import {
    Accordion,
    AccordionPanel,
    AccordionTitle,
    AccordionTitleItem,
    ChipAccordionPanel,
    NestedAccordionGrayBg,
} from '../Accordion';
import ClusterFilterAccordionItem, {
    ClusterFilterOption,
} from '../ClusterFilterAccordionItem';
import HealthStatFilterAccordionItem, {
    PriceHealthStatFilterOption,
    SelectHealthStatFilterOption,
} from '../HealthStatFilterAccordionItem';
import PresetFilterAccordionItem, {
    PresetFilterOption,
} from '../Accordion/PresetFilterAccordionItem/GooglePresetFilterAccordionItem';
import FlavorFilterAccordionItem from 'components/FlavorFilters/FlavorFilterAccordionItem';
import FlavorFilterV2AccordionItem from 'components/FlavorFilters/FlavorFilterV2AccordionItem';
import IconButton from '../Button/IconButton';
import LifeCycleFilterAccordionItem, {
    LifeCycleFilterOption,
} from '../LifeCycleFilterAccordionItem';
import { RemoveFilterButton } from '../Button/FilterButton';
import { PARENT_NAME_FILTER_KEYS } from 'utils/dataSources';

const OPTION_HEIGHT = '2rem';
const OPTION_SPACE = '.375rem';
const MAX_PANEL_HEIGHT = `calc(9 * (${OPTION_HEIGHT} + ${OPTION_SPACE}))`;

const MenuAccordionTitle = styled(AccordionTitle)`
    &::after {
        content: ':';
    }
`;

const MenuAccordionTitleItem = styled(AccordionTitleItem)`
    background-color: ${({ theme }) => theme.gray_7};
`;

const MenuFilterOption = css`
    ${({ theme, checked }) =>
        !checked
            ? `
            background-color: ${theme.white};
            border: 1px solid ${theme.gray_300};
        `
            : null}
    padding: ${({ theme }) => `${theme.space_3}px ${theme.space_6}px`};
    flex-basis: ${OPTION_HEIGHT};
    font-weight: ${({ theme }) => theme.fontBold};
`;

const MenuClusterFilterOption = styled(ClusterFilterOption)`
    ${MenuFilterOption}
`;
const MenuGroupFilterOption = styled(FilterCheckbox)`
    ${MenuFilterOption}
`;
const MenuHealthStatFilterOption = styled(SelectHealthStatFilterOption)`
    ${MenuFilterOption}
`;
const MenuPresetFilterOption = styled(PresetFilterOption)`
    ${MenuFilterOption}
`;
const MenuLifeCycleFilterOption = styled(LifeCycleFilterOption)`
    ${MenuFilterOption}
`;

const MenuAccordion = styled(Accordion)`
    background-color: ${({ theme }) => theme.gray_7};
    & > :first-of-type {
        border-top: 0;
    }
`;

const MenuFilterItemLabelWrapper = styled.span`
    color: ${({ theme }) => theme.gray_3};
    font-weight: ${({ theme }) => theme.fontMedium};
    text-transform: lowercase;
`;

const MenuFilterItemLabelName = styled.span`
    font-weight: ${({ theme }) => theme.fontBold};
    color: ${({ theme }) => theme.purple_1};
    text-transform: capitalize;
`;

function MenuFilterItemLabel({ name, abbrTitleHTML }) {
    return (
        <MenuFilterItemLabelWrapper>
            {abbrTitleHTML ? (
                <MenuFilterItemLabelName
                    dangerouslySetInnerHTML={{ __html: abbrTitleHTML }}
                />
            ) : (
                <MenuFilterItemLabelName>{name}</MenuFilterItemLabelName>
            )}{' '}
            filters
        </MenuFilterItemLabelWrapper>
    );
}

const MenuPanelCSS = css`
    background-color: ${({ theme }) => theme.gray_7};
`;

const MenuClusterFilterAccordionItemWithProps = styled(
    ClusterFilterAccordionItem,
).attrs(
    ({
        FilterOption = MenuClusterFilterOption,
        label = <MenuFilterItemLabel name="cluster" />,
        ...props
    }) => ({
        FilterOption,
        label,
        ...props,
    }),
)`
    ${MenuPanelCSS}
`;
const MenuClusterFilterAccordionItem = MenuClusterFilterAccordionItemWithProps;

const MenuLifeCycleFilterAccordionItemWithProps = styled(
    LifeCycleFilterAccordionItem,
).attrs(
    ({
        FilterOption = MenuLifeCycleFilterOption,
        label = <MenuFilterItemLabel name="life cycle" />,
        ...props
    }) => ({
        FilterOption,
        label,
        ...props,
    }),
)`
    ${MenuPanelCSS}
`;
const MenuLifeCycleFilterAccordionItem =
    MenuLifeCycleFilterAccordionItemWithProps;

const MenuHealthStatFilterAccordionItemWithProps = styled(
    HealthStatFilterAccordionItem,
).attrs(({ FilterOption = MenuHealthStatFilterOption, ...props }) => ({
    FilterOption,
    ...props,
}))`
    ${MenuPanelCSS}
`;
const MenuHealthStatFilterAccordionItem =
    MenuHealthStatFilterAccordionItemWithProps;

const MenuPresetFilterAccordionItemWithProps = styled(
    PresetFilterAccordionItem,
).attrs(({ FilterOption = MenuPresetFilterOption, ...props }) => ({
    FilterOption,
    ...props,
}))`
    ${MenuPanelCSS}
`;
const MenuPresetFilterAccordionItem = MenuPresetFilterAccordionItemWithProps;

const MenuChipAccordionPanel = styled(ChipAccordionPanel)`
    ${MenuPanelCSS}
`;

const MenuFilterAccordionButton = styled(FilterAccordionButton)`
    && {
        background-color: ${({ theme }) => theme.white};
    }
    &&[aria-expanded='true'] {
        background-color: ${({ theme }) => theme.gray_50};
    }
    padding: 0.75rem;
`;

const MenuAccordionPanelWithProps = styled(AccordionPanel).attrs(
    ({ $maxPanelHeight = MAX_PANEL_HEIGHT, ...props }) => ({
        $maxPanelHeight,
        ...props,
    }),
)`
    && {
        background-color: ${({ theme }) => theme.gray_50};
    }
    padding: 0 0.75rem 0.75rem;
`;
const MenuAccordionPanel = MenuAccordionPanelWithProps;

const SoloMenuAccordionPanelWithProps = styled(MenuAccordionPanel)`
    padding-top: 0.75rem;
    border-top-width: 0;
`;
const SoloMenuAccordionPanel = SoloMenuAccordionPanelWithProps;

function GroupFilterAccordionItem({
    mixpanelContext = 'GroupFilterSelect',
    FilterOption = MenuGroupFilterOption,
    label = <MenuFilterItemLabel name="group" />,
    queryData,
    showAllGroups = false,
    ...props
}) {
    const { data, ...restQuery } = useRelatedSearchGroupsWithData(queryData);
    const options = useMemo(
        () =>
            data
                ? data.map(({ name, id, volume }) => ({
                      label: name,
                      value: id,
                      volume,
                  }))
                : [],
        [data],
    );
    const visibleOptions = showAllGroups ? options : options.slice(6);

    return (
        <FilterAccordionItem
            options={visibleOptions}
            status={restQuery.status}
            noDataMessage={
                restQuery.status === 'error'
                    ? 'Error loading groups.'
                    : 'No groups available.'
            }
            mixpanelContext={mixpanelContext}
            FilterOption={FilterOption}
            label={label}
            {...props}
        />
    );
}

function TikTokGroupFilterAccordionItem({
    mixpanelContext = 'TikTokGroupFilterSelect',
    FilterOption = MenuGroupFilterOption,
    label = <MenuFilterItemLabel name="group" />,
    queryData,
    showAllGroups = false,
    ...props
}) {
    const { data, ...restQuery } = useFetchTikTokAffinityRoots(queryData);
    const options = useMemo(
        () =>
            data
                ? data.map(({ name, id, volume }) => ({
                      label: name,
                      value: id,
                      volume,
                  }))
                : [],
        [data],
    );
    const visibleOptions = showAllGroups ? options : options.slice(6);

    return (
        <FilterAccordionItem
            options={visibleOptions}
            status={restQuery.status}
            noDataMessage={
                restQuery.status === 'error'
                    ? 'Error loading groups.'
                    : 'No groups available.'
            }
            mixpanelContext={mixpanelContext}
            FilterOption={FilterOption}
            label={label}
            {...props}
        />
    );
}

function InstagramGroupFilterAccordionItem({
    mixpanelContext = 'InstagramGroupFilterSelect',
    FilterOption = MenuGroupFilterOption,
    label = <MenuFilterItemLabel name="group" />,
    queryData,
    showAllGroups = false,
    ...props
}) {
    const { data, ...restQuery } = useFetchInstagramRelatedAffinityRoots(
        queryData || {},
    );
    const options = useMemo(
        () =>
            (data || [])
                .map((item) => {
                    const groupValue = item?.name;
                    if (!groupValue) return null;
                    return {
                        label: groupValue,
                        value: groupValue,
                        volume: item?.volume,
                    };
                })
                .filter(Boolean),
        [data],
    );
    const visibleOptions = showAllGroups ? options : options.slice(6);
    return (
        <FilterAccordionItem
            options={visibleOptions}
            status={restQuery.status}
            noDataMessage={
                restQuery.status === 'error'
                    ? 'Error loading groups.'
                    : 'No groups available.'
            }
            mixpanelContext={mixpanelContext}
            FilterOption={FilterOption}
            label={label}
            {...props}
        />
    );
}

function FlavorsFilterAccordionItem({
    filters,
    setFilter,
    data,
    isLoadingData,
    filterMissingData,
    active,
    setActive,
    onChange,
    ...props
}) {
    const flavorsQuery = useFetchFlavors();
    const groupedFlavorsOptions =
        flavorsQuery.data?.groupedFlavorsOptions || {};
    return (
        <FlavorFilterAccordionItem
            {...props}
            isOpen={active?.includes('flavors')}
            toggleOpen={() =>
                setActive(active === 'flavors' ? null : 'flavors')
            }
            active={active}
            nestedToggleOpen={(val) =>
                setActive(active === val ? 'flavors' : val)
            }
            filters={filters}
            getOnChange={onChange}
            options={groupedFlavorsOptions}
            panelId="cards-filter-flavors"
            setFilter={setFilter}
            data={data}
            isLoadingData={isLoadingData}
            filterMissingData={filterMissingData}
            Icon={null}
            label={<MenuFilterItemLabel name="flavors" />}
            NestedAccordion={NestedAccordionGrayBg}
            $FilterAccordionNestedItemBgColor="white"
            FilterAccordionButton={MenuFilterAccordionButton}
        />
    );
}

const MenuFlavorsFilterAccordionItem = styled(FlavorsFilterAccordionItem)`
    ${MenuPanelCSS}
`;

function FlavorsFilterV2AccordionItem(props) {
    return (
        <FlavorFilterV2AccordionItem
            {...props}
            panelId="filter-flavor-groups"
            Icon={null}
            label={<MenuFilterItemLabel name="Flavor" />}
            NestedAccordion={NestedAccordionGrayBg}
            $FilterAccordionNestedItemBgColor="white"
            FilterAccordionButton={MenuFilterAccordionButton}
        />
    );
}

const MenuFlavorsV2FilterAccordionItem = styled(FlavorsFilterV2AccordionItem)`
    ${MenuPanelCSS}
`;

function PopIndexGroupFilterAccordionItem({
    mixpanelContext = 'PopIndexGroupFilterSelect',
    FilterOption = MenuGroupFilterOption,
    label = <MenuFilterItemLabel name="group" />,
    queryData,
    showAllGroups = false,
    ...props
}) {
    const { data, ...restQuery } = useFetchPopIndexAffinityRoots(queryData);
    const options = useMemo(
        () =>
            data
                ? data.map(({ name, id, volume }) => ({
                      label: name,
                      value: id,
                      volume,
                  }))
                : [],
        [data],
    );
    const visibleOptions = showAllGroups ? options : options.slice(6);

    return (
        <FilterAccordionItem
            options={visibleOptions}
            status={restQuery.status}
            noDataMessage={
                restQuery.status === 'error'
                    ? 'Error loading groups.'
                    : 'No groups available.'
            }
            mixpanelContext={mixpanelContext}
            FilterOption={FilterOption}
            label={label}
            {...props}
        />
    );
}

const Wrapper = styled(Popover)`
    position: relative;
    display: inline-flex;
    flex-direction: column;
`;

const FilterSelectPanel = styled(Popover.Panel)`
    position: absolute;
    z-index: 2;
    top: calc(100% + 2px);
    ${({ $menuAlign }) => `${$menuAlign}: 0;`}
    width: 21rem;
    border-radius: 5px;
    overflow: hidden;
    box-shadow: 0px 3px 8px rgba(0, 0, 0, 0.15);
`;

const TotalActiveFiltersBadge = styled.span`
    background-color: ${({ theme }) => theme.purple_1};
    color: ${({ theme }) => theme.gray_7};
    font-size: 0.875em;
    width: 1.43em;
    height: 1.43em;
    display: flex;
    align-items: center;
    justify-content: center;
    line-height: 1em;
    border-radius: 100px;
    margin-left: 6px;
`;

const MenuInstagramClusterFilterAccordionItem = forwardRef((props, ref) => {
    return (
        <MenuClusterFilterAccordionItem
            options={instagramClusterFilterOptions}
            {...props}
            ref={ref}
        />
    );
});

const MenuTikTokClusterFilterAccordionItem = forwardRef((props, ref) => {
    return (
        <MenuClusterFilterAccordionItem
            options={tikTokClusterFilterOptions}
            {...props}
            ref={ref}
        />
    );
});

const MenuClusterNonRisingFilterAccordionItem = forwardRef((props, ref) => {
    return (
        <MenuClusterFilterAccordionItem
            options={clusterFilterOptions.filter(
                ({ value }) => !value.includes('ris'),
            )}
            {...props}
            ref={ref}
        />
    );
});

const menuComponents = {
    popIndexParentName: PopIndexGroupFilterAccordionItem,
    tikTokParentName: TikTokGroupFilterAccordionItem,
    instagramParentName: InstagramGroupFilterAccordionItem,
    parentName: GroupFilterAccordionItem,
    cluster: MenuClusterFilterAccordionItem,
    tikTokCluster: MenuTikTokClusterFilterAccordionItem,
    instagramCluster: MenuInstagramClusterFilterAccordionItem,
    flavors: MenuFlavorsFilterAccordionItem,
    flavorGroups: MenuFlavorsV2FilterAccordionItem,
    life_cycle: MenuLifeCycleFilterAccordionItem,
};

function FilterSelect({
    filters,
    setFilter,
    setFilters,
    resetFilters,
    sortOption,
    setSortOption,
    initialSortOption,
    mixpanelMeta,
    menus,
    getOnChange,
    healthStatFilterOptions:
        defaultHealthStatFilterOptions = healthStatFilterOptions,
    showAllHealthStats = false,
    showPresetsMenu,
    nonRisingClustersOnly = false,
    queryData,
    menuAlign,
    appliedFilterChipCount = 0,
    showAllGroupOptions = false,
    ...props
}) {
    const defaultMenu =
        (showAllHealthStats && showPresetsMenu !== false) || showPresetsMenu
            ? 'preset'
            : menus[0] === 'healthStats'
              ? defaultHealthStatFilterOptions[0].label
              : menus[0];
    const [active, setActive] = useState(defaultMenu);
    const totalActiveFilters = Math.max(
        0,
        Object.entries(filters).reduce((acc, [key, value]) => {
            if (!(menus.includes(key) && value)) {
                return acc;
            }
            if (key === 'cluster' && nonRisingClustersOnly) {
                return [
                    ...acc,
                    ...value.filter(({ value }) => !value.includes('ris')),
                ];
            }
            return [...acc, ...value];
        }, []).length - (appliedFilterChipCount || 0),
    );

    const PresetAccordionItem = MenuPresetFilterAccordionItem;

    return (
        <Wrapper {...props}>
            <Popover.Button
                as={IconButton}
                IconLeft={MdSort}
                IconRight={FiChevronDown}
                id="filter-select-button"
            >
                Filters
                {totalActiveFilters > 0 ? (
                    <TotalActiveFiltersBadge>
                        {totalActiveFilters}
                    </TotalActiveFiltersBadge>
                ) : null}
            </Popover.Button>
            <FilterSelectPanel
                tabIndex="0"
                $showAllHealthStats={showAllHealthStats}
                $menuAlign={menuAlign}
            >
                <MenuAccordion $isActive={totalActiveFilters > 0 || !!active}>
                    {totalActiveFilters > 0 ? (
                        <MenuAccordionTitleItem>
                            <MenuAccordionTitle>
                                filters applied
                            </MenuAccordionTitle>
                            <RemoveFilterButton
                                onClick={() => resetFilters()}
                                css="margin-left: auto;"
                            >
                                {totalActiveFilters}
                            </RemoveFilterButton>
                        </MenuAccordionTitleItem>
                    ) : null}
                    {(showAllHealthStats && showPresetsMenu !== false) ||
                    showPresetsMenu ? (
                        <PresetAccordionItem
                            isOpen={active === 'preset'}
                            toggleOpen={() =>
                                setActive(
                                    active === 'preset'
                                        ? menus[
                                              (menus.indexOf('preset') + 1) %
                                                  menus.length
                                          ]
                                        : 'preset',
                                )
                            }
                            filters={filters}
                            setFilters={(nextFilters) => {
                                const { name } = filters;
                                if (name) {
                                    setFilters({ ...nextFilters, name });
                                } else {
                                    setFilters(nextFilters);
                                }
                            }}
                            sortOption={sortOption}
                            setSortOption={setSortOption}
                            initialSortOption={initialSortOption}
                            AccordionPanel={MenuAccordionPanel}
                            ChipAccordionPanel={MenuChipAccordionPanel}
                            FilterAccordionButton={MenuFilterAccordionButton}
                            showChipsWhenClosed={true}
                            Icon={null}
                            mixpanelMeta={mixpanelMeta}
                            label={
                                <MenuFilterItemLabel name="Popular Preset" />
                            }
                            panelId="cards-filter-popular-presets"
                            healthStatOptions={defaultHealthStatFilterOptions}
                        />
                    ) : null}
                    {menus.map((name, menuIdx) => {
                        if (name === 'healthStats' && showAllHealthStats) {
                            return defaultHealthStatFilterOptions.map(
                                (
                                    {
                                        label,
                                        filter: filterKey,
                                        options,
                                        panelId,
                                        abbrTitleHTML,
                                    },
                                    healthStatIdx,
                                ) => (
                                    <MenuHealthStatFilterAccordionItem
                                        isOpen={active === label}
                                        toggleOpen={() =>
                                            setActive(
                                                active === label
                                                    ? healthStatIdx + 1 <
                                                      defaultHealthStatFilterOptions.length
                                                        ? defaultHealthStatFilterOptions[
                                                              healthStatIdx + 1
                                                          ].label
                                                        : (menuIdx + 1) %
                                                                menus.length ===
                                                            menuIdx
                                                          ? defaultHealthStatFilterOptions[0]
                                                                .label
                                                          : menus[
                                                                (menuIdx + 1) %
                                                                    menus.length
                                                            ]
                                                    : label,
                                            )
                                        }
                                        appliedFilters={
                                            filters[filterKey]
                                                ? filters[filterKey]
                                                      .filter(({ value }) =>
                                                          options.some(
                                                              ({
                                                                  value: optionValue,
                                                              }) =>
                                                                  value ===
                                                                  optionValue,
                                                          ),
                                                      )
                                                      .map(({ value }) => value)
                                                : null
                                        }
                                        onChange={getOnChange(
                                            filterKey,
                                            filters,
                                            setFilter,
                                        )}
                                        label={
                                            <MenuFilterItemLabel
                                                name={label}
                                                abbrTitleHTML={abbrTitleHTML}
                                            />
                                        }
                                        panelId={`menu-filter-health-stats-${panelId}`}
                                        options={options}
                                        AccordionPanel={MenuAccordionPanel}
                                        ChipAccordionPanel={
                                            MenuChipAccordionPanel
                                        }
                                        FilterAccordionButton={
                                            MenuFilterAccordionButton
                                        }
                                        showChipsWhenClosed={true}
                                        Icon={null}
                                        mixpanelMeta={mixpanelMeta}
                                        key={label}
                                        space={OPTION_SPACE}
                                        {...(label === 'price point' && {
                                            FilterOption:
                                                PriceHealthStatFilterOption,
                                        })}
                                    />
                                ),
                            );
                        }
                        if (!menuComponents[name]) {
                            console.error(
                                `Not sure how to handle filter menu "${name}"`,
                            );
                            return null;
                        }

                        const Component =
                            name === 'cluster' && nonRisingClustersOnly
                                ? MenuClusterNonRisingFilterAccordionItem
                                : menuComponents[name];
                        return (
                            <Component
                                isOpen={active === name}
                                toggleOpen={() =>
                                    setActive(
                                        active === name
                                            ? menus[
                                                  (menus.indexOf(name) + 1) %
                                                      menus.length
                                              ]
                                            : name,
                                    )
                                }
                                onChange={getOnChange(name, filters, setFilter)}
                                appliedFilters={
                                    filters[name]
                                        ? filters[name].map(
                                              ({ value }) => value,
                                          )
                                        : null
                                }
                                AccordionPanel={
                                    menus.length > 1 || showAllHealthStats
                                        ? MenuAccordionPanel
                                        : SoloMenuAccordionPanel
                                }
                                ChipAccordionPanel={MenuChipAccordionPanel}
                                FilterAccordionButton={
                                    menus.length > 1
                                        ? MenuFilterAccordionButton
                                        : null
                                }
                                showChipsWhenClosed={showAllHealthStats}
                                Icon={null}
                                mixpanelMeta={mixpanelMeta}
                                panelId={`menu-filter-${name}`}
                                space={OPTION_SPACE}
                                key={`${name}-filter-menu`}
                                {...(name === 'instagramParentName' && {
                                    normalizeValue: (value) =>
                                        String(value || '')
                                            .toLowerCase()
                                            .trim(),
                                })}
                                {...([
                                    'flavorGroups',
                                    ...PARENT_NAME_FILTER_KEYS,
                                ].includes(name) && { queryData })}
                                {...(PARENT_NAME_FILTER_KEYS.includes(name) && {
                                    showAllGroups: showAllGroupOptions,
                                })}
                                {...(['flavors'].includes(name) && {
                                    filters,
                                    setFilter,
                                    data: props.data,
                                    isLoadingData: props.isLoadingData,
                                    filterMissingData: props.filterMissingData,
                                    active,
                                    setActive,
                                })}
                            />
                        );
                    })}
                </MenuAccordion>
            </FilterSelectPanel>
        </Wrapper>
    );
}

export default FilterSelect;
